// react
import { ReactNode, useContext } from 'react';

// context
import { ContextBanner } from '../../../App.context';

// types
import { ActionButton } from '../../../@types';

// mui
import {
	Alert,
	AlertProps,
	Button,
	Grid,
	IconButton,
	Typography,
	TypographyProps,
} from '@mui/material';

// icons
import {
	ClearOutlined as IconClear,
	InfoOutlined as IconInfo,
	TaskAltOutlined as IconTaskAlt,
	WarningAmberOutlined as IconWarning,
} from '@mui/icons-material';

// props
export interface BannerProps extends Omit<AlertProps, 'title'> {
	actions?: ActionButton[];
	hasClose?: boolean;
	isGlobal?: boolean;
	message: string;
	onClose?: () => void;
	title?: TypographyProps;
}

export const Banner = ({
	actions = [],
	hasClose,
	icon = true,
	message,
	onClose,
	severity = 'info',
	title,
	variant = 'filled',
	...props
}: BannerProps) => {
	// context
	const { setBanner } = useContext(ContextBanner);

	const icons: Record<string, ReactNode> = {
		error: <IconInfo />,
		info: <IconInfo />,
		success: <IconTaskAlt />,
		warning: <IconWarning />,
	};

	return (
		<Alert
			{...props}
			icon={false}
			severity={severity}
			sx={(theme) => {
				return {
					alignItems: 'center',
					borderRadius: 0,
					padding: actions.length ? theme.spacing(1.5, 3) : theme.spacing(1, 3),
					'& .MuiAlert-message': {
						width: '100%',
					},
				};
			}}
			variant={variant}>
			<Grid alignItems="center" justifyContent={{ md: 'space-between' }} container spacing={2}>
				<Grid item xs={true}>
					<Grid alignItems="center" container columnSpacing={2}>
						{icon && (
							<Grid item xs="auto">
								{icons[severity]}
							</Grid>
						)}
						<Grid item xs={true}>
							<Grid container spacing={{ xs: 0, md: 2 }}>
								{title && (
									<Grid item xs={12} md="auto">
										<Typography display="inline" fontWeight={600} {...title} />
									</Grid>
								)}
								<Grid item xs={12} md={true}>
									<Typography fontWeight={600} sx={{ opacity: title ? 0.75 : 1 }}>
										{message}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
				{(hasClose || actions.length > 0) && (
					<Grid item xs="auto">
						<Grid alignItems="center" container justifyContent="flex-end" spacing={2}>
							{actions?.map((action, i) => {
								const { children, ...props } = action;
								return (
									<Grid item key={`banner-action-${i}`} xs="auto">
										<Button {...props}>{children}</Button>
									</Grid>
								);
							})}
							{hasClose && (
								<Grid item xs="auto">
									<IconButton
										color="white"
										onClick={() => {
											setBanner(null);
											onClose?.();
										}}
										size="small">
										<IconClear />
									</IconButton>
								</Grid>
							)}
						</Grid>
					</Grid>
				)}
			</Grid>
		</Alert>
	);
};
